import { useEffect, useRef, useState } from 'react'
import { useOrgsApi } from 'src/api/db/apiOrgs'
import { useQuoteApi } from 'src/api/db/apiQuote'
import { useQuoteShippingLocationsApi } from 'src/api/db/apiQuoteShippingLocation'
import { useOrderNotifications } from 'src/api/server/useOrderNotifications'
import { myAccountRequestsPath } from 'src/routes/paths'
import {
  APPROVED_PROOFS_STATUS,
  STARTED_PRODUCTION_STATUS,
} from 'src/shared/consts/quoteStatus'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useRequestContext } from '../../../hooks/RequestContext'
import { useOrderViewState } from '../../../hooks/useOrderViewState'
import { buildIssuedInvoiceData } from '../../BillingDocument/utils'
import { hasBillingInfo } from '../CheckoutTab/components/PaymentOptions/utils'

export const useCheckoutDetails = () => {
  const { t } = useLocalization()
  const { quote, isDemo, demoShippingLocations, onDemoAdvance } =
    useRequestContext()
  const { setTab } = useOrderViewState(myAccountRequestsPath)
  const { organization } = useOrgsApi({})
  const [isStartingProduction, setIsStartingProduction] = useState(false)
  const [billingFormReady, setBillingFormReady] = useState(false)
  const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>(
    () =>
      demoShippingLocations
        ?.filter((location) => location.active)
        .map((location) => location.id) ?? []
  )
  const [
    locationNotesByShippingLocationId,
    setLocationNotesByShippingLocationId,
  ] = useState<Record<number, string>>({})
  const hydratedNotesForQuoteIdRef = useRef<number | null>(null)

  const quoteId = quote?.id ?? null
  const { updateQuote } = useQuoteApi({ skipListQuery: true })
  const {
    quoteShippingLocations,
    isLoading: isLoadingQuoteShippingLocations,
    upsertQuoteShippingLocations,
  } = useQuoteShippingLocationsApi({
    quote_id: quoteId,
  })
  const orderNotifications = useOrderNotifications()

  const persistedBillingReady = isDemo
    ? hasBillingInfo(quote?.organization ?? {})
    : organization?.data
      ? hasBillingInfo(organization.data)
      : false
  const detailsReady =
    (persistedBillingReady || billingFormReady) &&
    selectedLocationIds.length > 0
  const canPlaceOrder =
    detailsReady && quote?.status === APPROVED_PROOFS_STATUS

  useEffect(() => {
    hydratedNotesForQuoteIdRef.current = null
    setLocationNotesByShippingLocationId({})
  }, [quoteId])

  useEffect(() => {
    if (!quoteId) return
    if (isLoadingQuoteShippingLocations) return
    if (hydratedNotesForQuoteIdRef.current === quoteId) return
    setLocationNotesByShippingLocationId(
      Object.fromEntries(
        quoteShippingLocations.map((q) => [
          q.shipping_location_id,
          q.notes ?? '',
        ])
      )
    )
    hydratedNotesForQuoteIdRef.current = quoteId
  }, [quoteId, quoteShippingLocations, isLoadingQuoteShippingLocations])

  const handlePlaceOrder = async () => {
    if (isDemo) {
      onDemoAdvance?.('place_order')
      return
    }
    if (!quote) return

    setIsStartingProduction(true)
    try {
      await upsertQuoteShippingLocations.mutateAsync({
        quote_id: quote.id,
        shipping_location_ids: selectedLocationIds,
        notes_by_shipping_location_id: locationNotesByShippingLocationId,
      })

      const invoiceData = buildIssuedInvoiceData(quote)

      await updateQuote.mutateAsync({
        id: quote.id,
        status: STARTED_PRODUCTION_STATUS,
        invoice_data: invoiceData,
      })

      if (quote.public_id) {
        await orderNotifications.mutateAsync({
          kind: 'lifecycle',
          actorRole: 'customer',
          subject:
            t('🏭 Production started ', '🏭 Produktion startad ') +
            `#${quote.public_id}`,
          orderNotification: {
            quotePublicId: quote.public_id,
            headerBadge: t('Production started', 'Produktion startad'),
            headerTitle: t(
              'Order confirmed – production started',
              'Order bekräftad – produktion startad'
            ),
            message: t(
              'A customer has confirmed their order and production has started. Click below to view the order.',
              'En kund har bekräftat sin order och produktionen har startats. Klicka nedan för att visa ordern.'
            ),
            buttonText: t('View order', 'Se order'),
          },
        })
      }

      setTab('delivery')
    } finally {
      setIsStartingProduction(false)
    }
  }

  return {
    quote,
    selectedLocationIds,
    setSelectedLocationIds,
    locationNotesByShippingLocationId,
    setLocationNotesByShippingLocationId,
    detailsReady,
    canPlaceOrder,
    setBillingFormReady,
    isStartingProduction,
    handlePlaceOrder,
  }
}
